/* =============================================================================
   VIEWS / MENU-PICKER — Digitale Ankreuz-Speisekarte (v0.19.0)
   =============================================================================
   Setzt das Papier-Ankreuz-System (Burger/Bowls/Getränke-PDFs) 1:1 digital um.

   Konzept:
     - Browse-Bereich oben: Kategorien als Akkordeons mit Diät-Filter
     - Klick auf ein Hauptgericht (Burger/Bowl/Salat/Kinder) fügt eine
       Bestellzeile hinzu — wie ein neuer Bestell-Zettel pro Gast.
     - In der Bestellzeile: Brötchen-Pflichtwahl (Radio), Patty-Tausch
       (Klick auf Patty zeigt Alternativ-Pattys), Zutaten klickbar zum
       Streichen, +Extras, +Soßen, Mengen-Stepper, Löschen-Button.
     - Beilagen + Getränke + Soßen sind einfache Einzelpositionen
       (kein Konfigurations-Aufwand, nur Menge).
     - Allergie-/Sonderwunsch-Freitext + Live-Preis am Ende.

   Datenstruktur einer Bestellzeile:
     {
       lineId: 'l_…',
       ref: 'm_burg_02', name: 'CHEESEBURGER', category: 'Burger',
       qty: 1, unitPrice: 11.90,
       bun:     { id, name, extra } | null,
       patty:   { id, name } | null,
       dressing:{ id, name } | null,
       removedIngredients: ['zwiebeln'],
       extras:  [{ id, name, price }],
       sauces:  [{ id, name, price }],            // nur Beilagen-Zeilen
       sides:   [{ id, name, price }],            // S229 T1 — Beilagen als Combo
                                                  //   (Mehrfach) zu Burger/Bowl;
                                                  //   Legacy: side-Objekt (Deal/Alt)
       sideSauces: [{ id, name, price }],         // Soßen zur Combo-Beilage (1 Liste/Pos)
       lineTotal: 12.40
     }

   Persistierte Bestellung (in reservations.preorder, jsonb):
     { version: 2, items: [<line>...], specialRequests: '...', subtotal }

   Backward-Compat: alte `qty`-Plus-Minus-Logik in `customer-form.js`
   wurde entfernt (siehe Migration v0.19.0). Wenn jemand eine alte
   reservation.preorder vom Typ `[{id,name,price,qty}]` öffnet, rendert
   die Admin-View sie unverändert — wir verfälschen alte Daten nicht.

   Preis-Logik:
     - Abwahl von Zutaten → KEIN Rabatt (laut Definition).
     - Patty-Tausch → KEIN Aufpreis (gleiche Preisstufe).
     - Brötchen "glutenfrei" → +1,20 €.
     - Extras + Soßen → addiert.
============================================================================ */

import { htmlEscape, fmtEUR, showToast } from '../utils/helpers.js';
// S169 P3 — Deal-Kacheln + geführter Flow (self-registriert window-Handler).
import { mountDealTiles } from './deals-guest.js';
// S169 P3-Rework — Decomposition-Resolver (eine Deal-Position → order_items-Drafts).
// S198 Cut b3 — orderItemsToDealSelection = Pure Inverse fürs Deal-Re-Edit.
import { resolveDealToOrderItems, dealDraftsTotalCents, orderItemsToDealSelection } from '../core/deals.js';
// S69 REF-4 Cut 1/3: Pure-Helpers (Kategorisierung + Preis-Berechnung)
import {
  itemsByCategoryFromMenu,
  itemsBySubcategoryFromMenu,
  optionsByGroupFromMenu,
  addonsByGroupFromMenu,
  findItemFromMenu,
  dietTagBadges,
  recalcLine,
  subtotalOf,
  buildCartRequestFromLine
} from './menu-picker-helpers.js';
// S69 REF-4 Cut 2/3: Bestellzeilen-Render + Konfig-Modal (pure functions)
import {
  renderLineReceiptDetails,                       // pure
  renderModalFooter,                              // pure
  renderOrderLine as renderOrderLinePure,         // braucht state-wrap
  renderLineConfigBody as renderLineConfigBodyPure,
  renderDraftModal as renderDraftModalPure,
  renderKitchenNoteBlock                          // S197-Hotfix2 — Positions-Kommentar (Operator)
} from './menu-picker-render-line.js';
// S69 REF-4 Cut 3/3: alle mp*-Actions (window-onclick-Handler)
import {
  mpSetDietFilter,
  mpToggleCategory,
  mpAddItem,
  mpConfirmDraft,
  mpEditLine,
  mpCancelDraft,
  mpRemoveLine,
  mpChangeQty,
  mpSelectBun,
  mpSelectPatty,
  mpSelectDressing,
  mpSelectMilk,
  mpStrikeIngredient,
  mpTogglePatty,
  mpOpenPattySwap,
  mpClosePattySwap,
  mpSwapPatty,
  mpToggleExtra,
  mpToggleSide,
  mpToggleSideSauce,
  mpToggleSauce,
  mpSetSpecialRequests,
  mpSetLineNote
} from './menu-picker-actions.js';
// Re-Exports — externer Namespace-Konsument (main.js) sieht menu-picker.js
// weiterhin mit voller Public-API. Vermeidet Refactor von customer-form.js,
// takeaway-form.js, delivery-form.js — die importieren nur initMenuPicker /
// getOrder / validateOrder / resetMenuPicker direkt und sind unverändert.
export {
  mpSetDietFilter, mpToggleCategory, mpAddItem, mpConfirmDraft, mpEditLine,
  mpCancelDraft, mpRemoveLine, mpChangeQty, mpSelectBun, mpSelectPatty,
  mpSelectDressing, mpSelectMilk, mpStrikeIngredient, mpTogglePatty,
  mpOpenPattySwap, mpClosePattySwap, mpSwapPatty, mpToggleExtra,
  mpToggleSide, mpToggleSideSauce, mpToggleSauce, mpSetSpecialRequests,
  mpSetLineNote
} from './menu-picker-actions.js';

// -----------------------------------------------------------------------------
// Modul-State — als named export für menu-picker-actions.js (ESM live binding,
// Object-Mutationen propagieren ohne Re-Assignment)
// -----------------------------------------------------------------------------
export const _state = {
  lines: [],            // bestätigte Bestellzeilen im Warenkorb
  draftLine: null,      // Konfigurations-Modal: Item das gerade gepflegt wird
                        //   (lineId = '__draft__'); wird beim Bestätigen
                        //   nach lines verschoben, beim Abbrechen verworfen
  specialRequests: '',  // Allergie-/Sonderwunsch-Freitext
  dietFilter: 'all',    // 'all' | 'vegan' | 'vegetarisch' | 'glutenfrei' | 'halal'
  // S169 (Jonas) — Karte öffnet in der RUBRIKEN-ÜBERSICHT (alle Akkordeons zu),
  // nicht mehr automatisch „Burger" aufgeklappt. Genereller Standard.
  openCategories: new Set(),
  // S169 P3 — Deal-Positionen als separate VORSCHAU (bewusst NICHT in `lines`,
  // damit Submit/Snapshot/Summe unangetastet bleiben — der echte Bestellpfad +
  // Bon ist P4, §6.5). Jede: { lineId, dealId, dealName, summary, drafts, totalCents }.
  dealPreviews: [],
  rootId: 'menu-picker-root', // Container im DOM — kann ein anderer sein
                             // (z.B. 'takeaway-menu-root' im Takeaway-Modal).
  // S197 Cut b2 — Solo-Konfigurator (Re-Edit einer PERSISTIERTEN Position aus dem
  // Tisch-Warenkorb, ohne den ganzen Picker). { itemId, onConfirm(draft), onClose }.
  // Gesetzt → mpConfirmDraft/mpCancelDraft rufen den Callback statt _state.lines.
  soloEdit: null,
  // S244 Cut B — Tisch-Pfad: bestaetigte Zeilen gehen SOFORT an den Server
  // (table_cart_add) statt in `lines`. Der lokale Warenkorb bleibt leer, damit
  // es nur EINE Wahrheit gibt — den gemeinsamen Tisch-Warenkorb.
  onLineConfirmed: null
};

function getRoot() {
  return document.getElementById(_state.rootId);
}

let _lineCounter = 0;

/** Findet die aktive Bestellzeile — entweder die Draft im Modal oder
 *  eine bestätigte Zeile im Warenkorb. Alle Konfigurations-Handler
 *  greifen darauf zu, damit Modal und Warenkorb dieselben Mechaniken
 *  teilen. Exportiert für menu-picker-actions.js. */
export function findLine(lineId) {
  if (lineId === '__draft__' && _state.draftLine) return _state.draftLine;
  return _state.lines.find(l => l.lineId === lineId);
}

/** Exportiert für menu-picker-actions.js (state-aware Helper). */
export function getMenu() {
  const s = (window.App && window.App.storage && window.App.storage._getInternalState()) || {};
  return Array.isArray(s.menu) ? s.menu : [];
}

/** Exportiert für menu-picker-actions.js. */
export function newLineId() {
  _lineCounter += 1;
  return 'l_' + Date.now().toString(36) + '_' + _lineCounter;
}

// -----------------------------------------------------------------------------
// Helpers: state-aware Wrapper um Pure-Helpers (menu-picker-helpers.js).
// Behalten die ursprünglichen Aufruf-Signaturen aus menu-picker.js, damit
// Render-Layer + Actions + Tests unverändert weiterlaufen.
// -----------------------------------------------------------------------------
function itemsByCategory(category, opts) {
  return itemsByCategoryFromMenu(getMenu(), category, _state.dietFilter, opts);
}

function itemsBySubcategory(category) {
  return itemsBySubcategoryFromMenu(getMenu(), category, _state.dietFilter);
}

/** Exportiert für menu-picker-actions.js (für Default-Brötchen-Lookup in mpAddItem). */
export function optionsByGroup(group) {
  return optionsByGroupFromMenu(getMenu(), group);
}

function addonsByGroup(group, type) {
  return addonsByGroupFromMenu(getMenu(), group, type);
}

/** Exportiert für menu-picker-actions.js. */
export function findItem(id) {
  return findItemFromMenu(getMenu(), id);
}

function recalcAll() {
  _state.lines.forEach(recalcLine);
}

function subtotal() {
  // S169 P4 — Deal-Positionen sind echte Bestellpositionen → in die Summe.
  const deals = (_state.dealPreviews || []).reduce((s, p) => s + (Number(p.totalCents) || 0) / 100, 0);
  return +(subtotalOf(_state.lines) + deals).toFixed(2);
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
/**
 * @param {string} rootId
 * @param {object} [opts]
 * @param {function} [opts.onLineConfirmed]  S244 Cut B — wenn gesetzt, wandert
 *   eine bestaetigte Zeile NICHT in den lokalen Warenkorb, sondern geht als
 *   Warenkorb-Anfrage an den Aufrufer (Tisch-Pfad: sofort an `table_cart_add`).
 *   Single-Source ist dann der Server; der Picker bleibt leer.
 */
export function initMenuPicker(rootId, opts) {
  if (rootId && typeof rootId === 'string') _state.rootId = rootId;
  _state.onLineConfirmed = (opts && typeof opts.onLineConfirmed === 'function')
    ? opts.onLineConfirmed : null;
  const root = getRoot();
  if (!root) return;
  // S197-Hotfix3 (Jonas) — Speisekarten-Papier-Scope aus EINER Quelle: jeder
  // Menü-Mount bekommt automatisch `.menu-paper-scope` → die Light-Token-Regel in
  // styles.css greift, ohne dass eine neue Root-ID manuell in die CSS-Liste muss.
  root.classList.add('menu-paper-scope');
  // Default-Werte zurücksetzen, falls Form neu geöffnet wird.
  _state.lines = [];
  _state.draftLine = null;
  _state.specialRequests = '';
  _state.dietFilter = 'all';
  _state.openCategories = new Set();   // S169 — Rubriken-Übersicht (alle zu)
  _state.dealPreviews = [];
  bindEscOnce();
  renderAll();
}

export function getOrder() {
  recalcAll();
  return {
    version: 2,
    items: _state.lines.map(l => ({
      lineId: l.lineId,
      ref: l.ref,
      name: l.name,
      category: l.category,
      qty: l.qty,
      unitPrice: l.unitPrice,
      bun: l.bun || null,
      patty: l.patty || null,
      dressing: l.dressing || null,
      milk: l.milk || null,
      removedIngredients: l.removedIngredients || [],
      extras: l.extras || [],
      sauces: l.sauces || [],
      // S229 T1 — Mehrfach-Beilagen als sides[]. Legacy-Snapshots (side-Objekt)
      // bleiben über die tolerant lesenden Formatter (helpers.js) kompatibel.
      sides: l.sides || [],
      sideSauces: l.sideSauces || [],
      // S196 (Jonas) — Positions-Kommentar (Cockpit) mit durchreichen, sonst
      // droppt getOrder ihn vor lineToRpcItem → notes landet nie in order_items.
      note: l.note || '',
      lineTotal: l.lineTotal || 0
    })),
    specialRequests: (_state.specialRequests || '').trim(),
    subtotal: subtotal(),
    // S169 P4 — Deal-Positionen für die Snapshot-/Vorbestell-Anzeige (die echten
    // order_items kommen separat über getDealOrderItems() in die p_items).
    dealPositions: (_state.dealPreviews || []).map(p => ({
      dealId: p.dealId, dealName: p.dealName, summary: p.summary,
      lineUid: p.lineUid, totalCents: p.totalCents, components: p.drafts
    }))
  };
}

export function resetMenuPicker() {
  initMenuPicker();
}

/** Validierung: Pflicht-Optionen (Bowl-Patty + Dressing) gesetzt? */
export function validateOrder() {
  const errors = [];
  _state.lines.forEach((l, idx) => {
    const item = findItem(l.ref);
    if (!item) return;
    const meta = item.meta || {};
    if (meta.isBowl && meta.requiresPatty && !l.patty) {
      errors.push(`Zeile ${idx + 1} (${l.name}): bitte ein Patty wählen.`);
    }
    if (meta.isBowl && meta.requiresDressing && !l.dressing) {
      errors.push(`Zeile ${idx + 1} (${l.name}): bitte ein Dressing wählen.`);
    }
    if (meta.isBurger && !l.bun) {
      errors.push(`Zeile ${idx + 1} (${l.name}): bitte ein Brötchen wählen.`);
    }
  });
  return { ok: errors.length === 0, errors };
}

// -----------------------------------------------------------------------------
// Rendering: Top-Level
// -----------------------------------------------------------------------------
/** Exportiert für menu-picker-actions.js (Re-Render-Trigger). */
export function renderAll() {
  const root = getRoot();
  if (!root) return;
  recalcAll();
  if (_state.draftLine) recalcLine(_state.draftLine);
  root.innerHTML = `
    <div class="dg-host"></div>
    ${renderDietFilter()}
    ${renderBrowseSection()}
    ${renderDealPreviews()}
    ${renderOrderSection()}
    ${renderSpecialRequests()}
    ${renderSummary()}
    ${renderDraftModal()}
  `;
  // S169 P3 — Deal-Kacheln async laden + SCOPED in den .dg-host dieses Roots rendern
  // (kein globales getElementById → korrekt in JEDEM Mount: Reservierung/Takeaway/
  // Lieferung/Cockpit-Order-Entry). Gast: Fenster-gefiltert; Operator: Fenster ignoriert.
  try { mountDealTiles(root, { isOperator: _state.rootId === 'admin-order-menu-root' }); }
  catch (e) { console.warn('[menu-picker] deal tiles:', e && e.message); }
  // Body-Scroll sperren während Modal offen, damit der Hintergrund nicht
  // mitscrollt. Beim Schließen wieder freigeben.
  document.body.style.overflow = _state.draftLine ? 'hidden' : '';
}

// -----------------------------------------------------------------------------
// S169 P3 — Deal-Vorschau-Positionen (separat von `lines`; Submit unangetastet)
// -----------------------------------------------------------------------------
/** Aufgerufen von deals-guest.js („Übernehmen"). Fügt EINE Deal-Position als
 *  Vorschau hinzu (nicht in `lines` → kein Einfluss auf getOrder/Submit/Summe;
 *  der echte order_items-Pfad ist P4). */
export function addDealPosition(payload) {
  if (!payload || !Array.isArray(payload.drafts) || !payload.drafts.length) return;
  _lineCounter += 1;
  _state.dealPreviews.push({
    lineId: 'dl_' + _lineCounter + '_' + (payload.lineUid || ''),
    dealId: payload.dealId,
    dealName: payload.dealName,
    summary: payload.summary || '',
    drafts: payload.drafts,
    totalCents: Number(payload.totalCents) || 0
  });
  softRefresh();
}

export function removeDealPreview(lineId) {
  _state.dealPreviews = _state.dealPreviews.filter(p => p.lineId !== lineId);
  softRefresh();
}

/** S169 P4 — flache Liste der ECHTEN order_items-Drafts aller Deal-Positionen
 *  (camelCase, identisch zu lineToRpcItem-Output) → wird beim Bestellen an die
 *  p_items angehängt. Jede Komponente trägt customizations.deal.line_uid. */
export function getDealOrderItems() {
  const out = [];
  (_state.dealPreviews || []).forEach(p => (p.drafts || []).forEach(d => out.push(d)));
  return out;
}

/** Render-Block der Deal-Vorschau-Positionen. Immer ein Container (ggf. leer),
 *  damit softRefresh ihn granular ersetzen kann. */
function renderDealPreviews() {
  const previews = _state.dealPreviews || [];
  if (!previews.length) return `<div class="mp-deal-previews"></div>`;
  const cards = previews.map(p => `
    <div class="mp-deal-card" data-line="${p.lineId}">
      <div class="mp-deal-card-head">
        <span class="mp-deal-badge">Aktion</span>
        <span class="mp-deal-title">${htmlEscape(p.dealName)}</span>
        <span class="mp-deal-price">${fmtEUR((p.totalCents || 0) / 100)}</span>
        <button type="button" class="mp-line-remove" onclick="removeDealPreview('${p.lineId}')" title="Entfernen">✕</button>
      </div>
      ${p.summary ? `<div class="mp-deal-sum">${htmlEscape(p.summary)}</div>` : ''}
      <div class="mp-deal-comps">
        ${p.drafts.map(d => `<div class="mp-deal-comp"><span>${htmlEscape(d.name)}</span><span>${fmtEUR((d.unitPriceCents || 0) / 100)} · ${d.taxRate}%</span></div>`).join('')}
      </div>
    </div>`).join('');
  return `
    <div class="mp-deal-previews">
      <h3 class="mp-order-head">Aktions-Menü <span class="mp-order-count">${previews.length} Position${previews.length === 1 ? '' : 'en'}</span></h3>
      ${cards}
    </div>`;
}

/** Granulares Update — verwendet von allen Konfigurations-Handlern
 *  (mpSelectBun, mpStrikeIngredient, mpToggleExtra, mpChangeQty, …),
 *  damit das Modal nicht bei jedem Klick neu in den DOM gemountet wird
 *  und die Open-Animation re-triggert. Mountet das Modal genau einmal
 *  beim Öffnen, aktualisiert danach nur Body+Footer in-place. Im
 *  Warenkorb ersetzt es nur die Order-Section + Summary. */
/** Exportiert für menu-picker-actions.js (Re-Render-Trigger nach mp*-Action). */
export function softRefresh() {
  const root = getRoot();
  if (!root) return;
  recalcAll();
  if (_state.draftLine) recalcLine(_state.draftLine);

  // --- Modal-Sync (Mount/Unmount/Inline-Update) ---
  let overlay = root.querySelector('.mp-modal-overlay');
  if (_state.draftLine) {
    const item = findItem(_state.draftLine.ref);
    if (!overlay && item) {
      // Modal erstmalig öffnen — voller HTML-Insert mit Open-Animation
      root.insertAdjacentHTML('beforeend', renderDraftModal());
      document.body.style.overflow = 'hidden';
    } else if (overlay && item) {
      // Modal existiert — nur Body und Footer inkrementell ersetzen
      const bodyEl   = overlay.querySelector('.mp-modal-body');
      const footerEl = overlay.querySelector('.mp-modal-footer');
      // S169 P3-Rework — deal-aware (Deal-Draft: Body + Deal-Sektion, Deal-Footer).
      if (bodyEl)   bodyEl.innerHTML = renderModalBodyInner(_state.draftLine, item);
      if (footerEl) replaceElementWithHTML(footerEl, renderModalFooterInner(_state.draftLine, item));
    }
  } else if (overlay) {
    // Draft beendet (Confirm oder Cancel) — Modal entfernen
    overlay.remove();
    document.body.style.overflow = '';
  }

  // --- Deal-Vorschau (granular) ---
  const oldDeals = root.querySelector('.mp-deal-previews');
  if (oldDeals) replaceElementWithHTML(oldDeals, renderDealPreviews());

  // --- Warenkorb + Summary (granular, ohne Browse-/Diet-/Special-Re-Render) ---
  const oldOrder = root.querySelector('.mp-order, .mp-order-empty');
  if (oldOrder) replaceElementWithHTML(oldOrder, renderOrderSection());
  const oldSum = root.querySelector('.mp-summary');
  if (oldSum) replaceElementWithHTML(oldSum, renderSummary());
}

/** Helper: ersetzt ein Element durch das parsed-erste-Kind des neuen HTML.
 *  Wir bauen kurz einen Wrapper, weil insertAdjacentHTML auf das Element
 *  selbst nicht möglich ist (nur auf seine Position). */
function replaceElementWithHTML(el, html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html.trim();
  const next = tmp.firstElementChild;
  if (next) el.replaceWith(next);
}

// ESC-Taste schließt das Konfigurations-Modal. Wird einmalig beim
// Modul-Load registriert; mehrfach-Init bei z.B. resetMenuPicker ist
// kein Problem, weil derselbe Listener via `_escBound` geguarded ist.
let _escBound = false;
function bindEscOnce() {
  if (_escBound || typeof document === 'undefined') return;
  _escBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && _state.draftLine) {
      e.preventDefault();
      mpCancelDraft();
    }
  });
}

function renderDietFilter() {
  const filters = [
    { v: 'all',          label: 'Alle anzeigen' },
    { v: 'vegan',        label: 'Vegan' },
    { v: 'vegetarisch',  label: 'Vegetarisch' },
    { v: 'glutenfrei',   label: 'Glutenfrei' },
    { v: 'halal',        label: 'Halal' }
  ];
  return `
    <div class="mp-diet-bar">
      <span class="mp-diet-label">Filter:</span>
      ${filters.map(f => `
        <button type="button"
          class="mp-diet-chip ${_state.dietFilter === f.v ? 'active' : ''}"
          onclick="mpSetDietFilter('${f.v}')">${htmlEscape(f.label)}</button>
      `).join('')}
    </div>
  `;
}

function renderBrowseSection() {
  const cats = ['Burger', 'Bowls', 'Salate', 'Beilagen', 'Soßen', 'Getränke', 'Kinder'];
  return `
    <div class="mp-browse">
      <h3 class="mp-browse-head">Speisekarte — tippe zum Hinzufügen</h3>
      ${cats.map(cat => renderCategoryAccordion(cat)).join('')}
    </div>
  `;
}

function renderCategoryAccordion(cat) {
  const items = itemsByCategory(cat);
  if (items.length === 0) return '';
  const isOpen = _state.openCategories.has(cat);
  return `
    <div class="mp-cat ${isOpen ? 'open' : ''}">
      <button type="button" class="mp-cat-head" onclick="mpToggleCategory('${cat}')">
        <span class="mp-cat-name">${htmlEscape(cat)}</span>
        <span class="mp-cat-count">${items.length}</span>
        <span class="mp-cat-caret">${isOpen ? '▾' : '▸'}</span>
      </button>
      ${isOpen ? `<div class="mp-cat-body">${renderCategoryBody(cat)}</div>` : ''}
    </div>
  `;
}

function renderCategoryBody(cat) {
  if (cat === 'Burger' || cat === 'Bowls' || cat === 'Salate' || cat === 'Kinder') {
    const groups = itemsBySubcategory(cat);
    return groups.map(([sub, items]) => `
      ${sub ? `<div class="mp-sub-head">${htmlEscape(sub)}</div>` : ''}
      <div class="mp-browse-grid">
        ${items.map(renderBrowseCard).join('')}
      </div>
    `).join('');
  }
  if (cat === 'Beilagen' || cat === 'Soßen') {
    const items = itemsByCategory(cat);
    return `<div class="mp-browse-grid">${items.map(renderBrowseCard).join('')}</div>`;
  }
  if (cat === 'Getränke') {
    const groups = itemsBySubcategory(cat);
    return groups.map(([sub, items]) => `
      ${sub ? `<div class="mp-sub-head">${htmlEscape(sub)}</div>` : ''}
      <div class="mp-browse-grid">
        ${items.map(renderBrowseCard).join('')}
      </div>
    `).join('');
  }
  return '';
}

function renderBrowseCard(item) {
  const unit = item.unitLabel ? ` <span class="mp-unit">${htmlEscape(item.unitLabel)}</span>` : '';
  // S196 Cut c — Kachel zeigt eine Zutaten-Vorschau (menu_items.ingredients,
  // Anzeige-Zutaten), damit der Gast ohne Öffnen sieht, was drauf ist. Statt der
  // Diät-Badges (die bleiben im Anpassen-Modal). Fallback auf description, wenn
  // keine Zutaten gepflegt sind (Getränke/Beilagen) — keine Regression.
  const ingredients = Array.isArray(item.ingredients) ? item.ingredients.filter(Boolean) : [];
  const previewHtml = ingredients.length
    ? `<div class="mp-card-ingredients">${htmlEscape(ingredients.join(', '))}</div>`
    : (item.description ? `<div class="mp-card-desc">${htmlEscape(item.description)}</div>` : '');
  // Preis rechts, vertikal mittig (2-Spalten) — „macht das Bild rund" (Jonas).
  return `
    <button type="button" class="mp-browse-card mp-browse-card--split"
            onclick="mpAddItem('${item.id}')"
            title="Zur Bestellung hinzufügen">
      <div class="mp-card-main">
        <div class="mp-card-name">${htmlEscape(item.name)}${unit}</div>
        ${previewHtml}
      </div>
      <span class="mp-card-price">${fmtEUR(item.price)}</span>
    </button>
  `;
}

// -----------------------------------------------------------------------------
// Rendering: Bestellungs-Bereich
// -----------------------------------------------------------------------------
function renderOrderSection() {
  if (_state.lines.length === 0) {
    return `
      <div class="mp-order-empty">
        <div class="mp-order-empty-icon">🍔</div>
        <p>Noch nichts in der Bestellung. Tippe oben auf ein Gericht, um anzufangen.</p>
      </div>
    `;
  }
  return `
    <div class="mp-order">
      <h3 class="mp-order-head">Deine Vorbestellung
        <span class="mp-order-count">${_state.lines.length} Position${_state.lines.length === 1 ? '' : 'en'}</span>
      </h3>
      ${_state.lines.map(renderOrderLine).join('')}
    </div>
  `;
}

/** state-aware Wrapper um renderOrderLinePure (menu-picker-render-line.js).
 *  Behält die ursprüngliche Aufruf-Signatur für interne Konsumenten. */
function renderOrderLine(line) {
  return renderOrderLinePure(line, getMenu());
}

// renderLineReceiptDetails ist pure und kommt direkt aus dem Sub-Modul.
// (Re-Imported am Modul-Anfang.)

/** state-aware Wrapper um renderLineConfigBodyPure (menu-picker-render-line.js).
 *  Injiziert getMenu() + _state.dietFilter; alle internen Aufrufer rufen
 *  weiterhin renderLineConfigBody(line, item) ohne ctx-Argument. */
function renderLineConfigBody(line, item) {
  return renderLineConfigBodyPure(line, item, getMenu(), _state.dietFilter);
}

// -----------------------------------------------------------------------------
// Konfigurations-Modal (Pop-Up vor dem Warenkorb-Add)
// -----------------------------------------------------------------------------
/** state-aware Wrapper um renderDraftModalPure (menu-picker-render-line.js).
 *  Injiziert _state.draftLine + getMenu() + _state.dietFilter. */
/** S196/S197 — Operator-Kontext (Positions-Kommentar sichtbar): Cockpit-Order-Entry
 *  ODER Solo-Re-Edit aus dem Tisch-Warenkorb. Gast (andere rootIds) → false.
 *  Single-Source für renderDraftModal (Voll-Render) + renderModalBodyInner
 *  (softRefresh-Body-Ersatz) — sonst verschwindet das Kommentar-Feld nach dem
 *  ersten Options-Klick (S197-Hotfix2). */
function _isOperatorContext() {
  return _state.rootId === 'admin-order-menu-root' || !!_state.soloEdit;
}

function renderDraftModal() {
  const line = _state.draftLine;
  if (line && line.deal) return renderDealDraftModal(line);
  return renderDraftModalPure(line, getMenu(), _state.dietFilter, _isOperatorContext());
}

// -----------------------------------------------------------------------------
// S197 Cut b2 — Solo-Konfigurator: den Anpassen-Konfigurator STANDALONE öffnen,
// um eine bereits persistierte Warenkorb-Position zu re-editieren (ohne den
// ganzen Picker/Order-Entry-Flow). Wiederverwendet Draft-Modal + alle mp*-Handler
// (die über _state.draftLine / findLine('__draft__') laufen). Confirm/Cancel
// werden in mpConfirmDraft/mpCancelDraft anhand _state.soloEdit abgezweigt.
// -----------------------------------------------------------------------------
const SOLO_HOST_ID = 'tc-reedit-host';

/** Öffnet den Konfigurator solo mit einer vorbefüllten Draft-Line.
 *  @param {object} line  Draft-Line (aus orderItemToLine, lineId '__draft__')
 *  @param {object} opts  { itemId, onConfirm(draft)=>Promise, onClose() } */
export function openSoloConfigurator(line, opts) {
  if (!line) return;
  let host = document.getElementById(SOLO_HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = SOLO_HOST_ID;
    // S197-Hotfix3 — Speisekarten-Papier-Scope (openSoloConfigurator läuft NICHT
    // über initMenuPicker → Klasse hier explizit setzen; sonst erbt das Config-
    // Modal die Dark-Tokens = washed-out).
    host.className = 'tc-reedit-host menu-paper-scope';
    document.body.appendChild(host);
  } else {
    host.classList.add('menu-paper-scope');
  }
  _state.rootId = SOLO_HOST_ID;
  _state.lines = [];
  _state.dealPreviews = [];
  _state.soloEdit = {
    itemId:   (opts && opts.itemId) || null,
    onConfirm: opts && opts.onConfirm,
    onClose:   opts && opts.onClose
  };
  // _isEdit → Footer-Button „Speichern" (statt „In den Warenkorb").
  line._isEdit = true;
  line._persistedItemId = (opts && opts.itemId) || null;
  _state.draftLine = line;
  recalcLine(line);
  bindEscOnce();
  host.innerHTML = renderDraftModal();
  document.body.style.overflow = 'hidden';
}

/** Schließt den Solo-Konfigurator (Modal weg, State sauber). KEIN Callback hier
 *  — mpConfirmDraft/mpCancelDraft rufen onConfirm/onClose selbst. */
export function closeSoloConfigurator() {
  const host = document.getElementById(SOLO_HOST_ID);
  if (host && host.parentNode) host.parentNode.removeChild(host);
  _state.draftLine = null;
  _state.soloEdit = null;
  document.body.style.overflow = '';
}

// -----------------------------------------------------------------------------
// S198 Cut b3 — Solo-DEAL-Konfigurator: eine persistierte Deal-Position (Food +
// optional Drink, verklammert über line_uid) vorbefüllt re-editieren. Baut aus dem
// order_items-Paar via Pure-Inverse (orderItemsToDealSelection) die selection, daraus
// die Draft-Line mit `line.deal` (Preise NEU aus der aktuellen dealDetail gerechnet,
// konsistent mit b2 „Re-Edit-Preis = aktueller Preis"), und hostet sie über
// openSoloConfigurator (renderDraftModal routet line.deal → renderDealDraftModal).
// Confirm läuft über mpConfirmDealDraft (dortiger _state.soloEdit-Abzweig).
// -----------------------------------------------------------------------------

/** selection (Pure Inverse) + aktuelle dealDetail → Draft-Line mit `line.deal`.
 *  Spiegelt die Zeilen-Konstruktion aus mpOpenDealDraft, aber vorbefüllt. */
function _dealLineFromSelection(sel, dealDetail) {
  if (!sel || !dealDetail) return null;
  const groups = Array.isArray(dealDetail.groups) ? dealDetail.groups : [];
  const group = groups.find(g => (g.id ?? g.group_id) === sel.groupId);
  if (!group) return null;
  const addons = Array.isArray(dealDetail.addons) ? dealDetail.addons : [];
  const menu = getMenu();
  const midOf = it => (it.menu_item_id ?? it.menuItemId);
  const c = sel.customizations || {};

  // Beilage: aktuelle Stufe (price_cents) aus dem passenden side-Add-on ziehen.
  let side = c.side || null;
  if (side && side.id) {
    const a = addons.find(ad => (ad.kind ?? ad.addon_kind) === 'side'
      && (Array.isArray(ad.items) ? ad.items : []).some(it => midOf(it) === side.id));
    if (a) side = { id: side.id, name: side.name, price: (Number(a.price_cents ?? a.priceCents) || 0) / 100 };
  }
  // Drink: aktuelle Stufe aus dem gewählten Drink-Add-on.
  let dealDrink = null;
  if (sel.drink && sel.drink.itemId) {
    const a = addons.find(ad => (ad.id ?? ad.addon_id) === sel.drink.addonId)
      || addons.find(ad => (ad.kind ?? ad.addon_kind) === 'drink'
        && (Array.isArray(ad.items) ? ad.items : []).some(it => midOf(it) === sel.drink.itemId));
    if (a) dealDrink = { addonId: (a.id ?? a.addon_id), itemId: sel.drink.itemId, price: (Number(a.price_cents ?? a.priceCents) || 0) / 100 };
  }
  const mi = menu.find(m => m.id === sel.mainItemId) || null;
  return {
    lineId: '__draft__', ref: sel.mainItemId,
    name:     (mi && mi.name) || sel.mainName || '',
    category: (mi && mi.category) || sel.mainCategory || '',
    qty:      Math.max(1, Number(sel.qty) || 1),
    unitPrice: (mi && Number(mi.price)) || 0,
    bun: c.bun || null, patty: c.patty || null, dressing: c.dressing || null, milk: c.milk || null,
    removedIngredients: Array.isArray(c.removedIngredients) ? c.removedIngredients.slice() : [],
    extras:             Array.isArray(c.extras) ? c.extras.slice() : [],
    sauces:             Array.isArray(c.sauces) ? c.sauces.slice() : [],
    side,
    sideSauces:         Array.isArray(c.sideSauces) ? c.sideSauces.slice() : [],
    note: (sel.notes != null ? (sel.notes || '') : ''),   // S198-HF1 — Kommentar vorbefüllen
    dealDrink,
    deal: {
      dealId: dealDetail.id, dealName: dealDetail.name,
      groupId: sel.groupId, lineUid: sel.lineUid,
      dealPriceCents: Number(group.deal_price_cents ?? group.dealPriceCents) || 0,
      rules: group.rules || {},
      dealAddons: addons, dealDetail
    }
  };
}

/** Öffnet den Deal-Konfigurator solo, vorbefüllt aus dem persistierten Paar.
 *  @param {Array}  rows        order_items-Paar (gleicher line_uid; DB- oder RPC-Shape)
 *  @param {object} dealDetail  frische deal_detail(id)-jsonb (Operator-Pfad)
 *  @param {object} opts        { lineUid, onConfirm({drafts,lineUid})=>Promise, onClose() }
 *  @returns {boolean} true wenn geöffnet, false wenn nicht auflösbar (Aufrufer → Hinweis). */
export function openDealSoloConfigurator(rows, dealDetail, opts) {
  const sel = orderItemsToDealSelection(rows, dealDetail);
  if (!sel) return false;
  const line = _dealLineFromSelection(sel, dealDetail);
  if (!line) return false;
  openSoloConfigurator(line, opts);
  return true;
}

/* -------------------------------------------------------------------------- */
/* S169 P3-Rework — Deal-Draft: gewohntes Anpassen-Modal + Deal-Erweiterung     */
/* -------------------------------------------------------------------------- */

/** menu_item_id → {name, category, itemType} (für den Resolver). */
function _menuIndex() {
  const idx = {};
  getMenu().forEach(m => { idx[m.id] = { name: m.name, category: m.category, itemType: m.itemType }; });
  return idx;
}

/** Baut aus der Draft-Zeile (deal) die order_items-Drafts via Pure-Core-Resolver.
 *  S169-Korrektur: Beilage/Dips/Soßen liegen in den NORMALEN Keys (line.side/
 *  line.sideSauces/line.sauces) → der Resolver erzeugt 1 Food-order_item (wie
 *  Normalbestellung) + optional eine separate Drink-Zeile. */
function _dealDraftsFromLine(line) {
  const deal = line && line.deal;
  if (!deal) return null;
  const selection = {
    groupId: deal.groupId, mainItemId: line.ref, qty: line.qty || 1, lineUid: deal.lineUid,
    notes: line.note || null,   // S198-HF1 — Positions-Kommentar → Food-Zeile
    customizations: {
      bun: line.bun || null,
      patty: line.patty || null,
      dressing: line.dressing || null,
      milk: line.milk || null,
      removedIngredients: line.removedIngredients || [],
      extras: line.extras || [],
      sauces: line.sauces || [],
      side: line.side || null,
      sideSauces: line.sideSauces || []
    },
    drink: line.dealDrink ? { addonId: line.dealDrink.addonId, itemId: line.dealDrink.itemId } : null
  };
  try { return resolveDealToOrderItems(selection, deal.dealDetail, _menuIndex()); }
  catch (e) { console.warn('[deal] resolve:', e && e.message); return null; }
}

/** Deal-Erweiterungs-Sektion im Anpassen-Modal: Beilage + Getränk (deal_addons)
 *  + „Soßen inkl."-Hinweis + Live-Bon-Vorschau (je Komponente eigener tax_rate). */
function renderDealSection(line) {
  const deal = line.deal; if (!deal) return '';
  const menu = getMenu();
  const nameOf = id => { const m = menu.find(x => x.id === id); return m ? m.name : id; };
  const addons = deal.dealAddons || [];
  const sides = addons.filter(a => a.kind === 'side');
  const drinks = addons.filter(a => a.kind === 'drink');

  // Beilage (Stufen) → schreibt in den NORMALEN Key line.side {id,name,price};
  // die Beilagen-Stufenpreise speisen den Optionsumfang (Deal-Preis-Logik in
  // recalcLine/Resolver). „Ohne" = line.side=null.
  const sideOn = !line.side;
  const sideOpts = [`<button type="button" class="mp-addon-chip ${sideOn ? 'active' : ''}" onclick="mpSetDealSide('','')">Ohne</button>`];
  sides.forEach(a => (a.items || []).forEach(it => {
    const on = line.side && line.side.id === it.menu_item_id;
    sideOpts.push(`<button type="button" class="mp-addon-chip ${on ? 'active' : ''}" onclick="mpSetDealSide('${it.menu_item_id}','${a.price_cents}','${a.id || a.addon_id || ''}')">${on ? '✓ ' : '+ '}${htmlEscape(nameOf(it.menu_item_id))} <small>+${fmtEUR((a.price_cents || 0) / 100)}</small></button>`);
  }));
  const sideBlock = sides.length
    ? `<div class="mp-row mp-row-addons"><span class="mp-row-label">Beilage dazu?</span><div class="mp-addon-list">${sideOpts.join('')}</div></div>`
    : '';

  // Beilagen-Dips → NORMALER Key line.sideSauces (max. 2, inkl. 0 €), aus der
  // bestehenden Soßen-Kategorie. Nur sichtbar, wenn eine Beilage gewählt ist.
  const sideSauceBlock = line.side ? (() => {
    const saucesOpts = addonsByGroup(null, 'sauce');
    if (!saucesOpts.length) return '';
    const sel = line.sideSauces || [];
    const atLimit = sel.length >= 2;
    const chips = saucesOpts.map(s => {
      const on = sel.some(x => x.id === s.id);
      const dis = !on && atLimit;
      return `<button type="button" class="mp-addon-chip ${on ? 'active' : ''}"${dis ? ' disabled' : ''} onclick="mpToggleDealSideSauce('${s.id}')">${on ? '✓ ' : '+ '}${htmlEscape(s.name)}</button>`;
    });
    return `<div class="mp-row mp-row-addons"><span class="mp-row-label">Soßen zur Beilage <small>(max. 2, inkl.)</small></span><div class="mp-addon-list">${chips.join('')}</div></div>`;
  })() : '';

  // Getränk (optional) → separate normale order_item-Zeile (19 %).
  const drinkOn = !line.dealDrink;
  const drinkOpts = [`<button type="button" class="mp-addon-chip ${drinkOn ? 'active' : ''}" onclick="mpSetDealDrink('','','')">Ohne</button>`];
  drinks.forEach(a => (a.items || []).forEach(it => {
    const on = line.dealDrink && line.dealDrink.addonId === a.id && line.dealDrink.itemId === it.menu_item_id;
    drinkOpts.push(`<button type="button" class="mp-addon-chip ${on ? 'active' : ''}" onclick="mpSetDealDrink('${a.id}','${it.menu_item_id}','${a.price_cents}')">${on ? '✓ ' : '+ '}${htmlEscape(nameOf(it.menu_item_id))} <small>+${fmtEUR((a.price_cents || 0) / 100)}</small></button>`);
  }));
  const drinkBlock = drinks.length
    ? `<div class="mp-row mp-row-addons"><span class="mp-row-label">Getränk dazu?</span><div class="mp-addon-list">${drinkOpts.join('')}</div></div>`
    : '';

  const saucesNote = (deal.rules && deal.rules.sauces_included) ? `<div class="dg-hint" style="margin:4px 0 0;">Soßen inklusive.</div>` : '';
  const drafts = _dealDraftsFromLine(line);
  const preview = drafts ? `<div class="dg-preview"><div class="dg-preview-head">Das kommt auf den Bon</div>${drafts.map(d => `<div class="dg-preview-row"><span>${htmlEscape(d.name)}</span><span>${fmtEUR((d.unitPriceCents || 0) / 100)} · ${d.taxRate}%</span></div>`).join('')}</div>` : '';
  return `
    <div class="mp-deal-extra">
      <div class="mp-deal-extra-head">Aktions-Menü</div>
      ${sideBlock}
      ${sideSauceBlock}
      ${drinkBlock}
      ${saucesNote}
      ${preview}
    </div>`;
}

/** Body = gewohnte Anpassen-UI (renderLineConfigBody, Brötchen ggf. Whitelist-
 *  gefiltert) + Deal-Sektion. Footer = Deal-Aktionspreis + „Übernehmen". */
function renderModalBodyInner(line, item) {
  const base = renderLineConfigBodyPure(line, item, getMenu(), _state.dietFilter,
    line.deal ? { dealMode: true, bunWhitelist: line.deal.rules && line.deal.rules.bun_whitelist } : undefined);
  // S197-Hotfix2 — der inkrementelle softRefresh-Body-Ersatz muss den Positions-
  // Kommentar (Operator-Kontext) MIT anhängen, sonst ist er nach dem ersten
  // Options-Klick weg (Voll-Render renderDraftModalPure hängt ihn an, dieser Pfad
  // hing ihn nicht an).
  // S198-HF1 (Jonas): auch der DEAL-Draft bekommt im Operator-Kontext das Kommentar-
  // Feld (Anlage im Cockpit + b3-Re-Edit) — die Notiz landet auf der Food-Zeile
  // (resolveDealToOrderItems → foodNotes). Gast (andere rootId) bleibt ohne Feld.
  const note = _isOperatorContext() ? renderKitchenNoteBlock(line) : '';
  if (line.deal) return base + renderDealSection(line) + note;
  return base + note;
}
function renderModalFooterInner(line, item) {
  return line.deal ? renderDealFooter(line) : renderModalFooter(line, item);
}

function renderDealFooter(line) {
  return `
    <div class="mp-modal-footer">
      <div class="mp-modal-qty">
        <span class="mp-modal-qty-label">Menge</span>
        <div class="mp-qty">
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', -1)" aria-label="Weniger">−</button>
          <span class="mp-qty-val">${line.qty}</span>
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', 1)" aria-label="Mehr">+</button>
        </div>
      </div>
      <div class="mp-modal-total">
        <span class="mp-modal-total-label">Aktionspreis</span>
        <span class="mp-modal-total-value">${fmtEUR(line.lineTotal)}</span>
      </div>
      <div class="mp-modal-actions">
        <button type="button" class="btn btn-ghost" onclick="mpCancelDraft()">Abbrechen</button>
        <button type="button" class="btn btn-primary" onclick="mpConfirmDealDraft()">Übernehmen</button>
      </div>
    </div>`;
}

function renderDealDraftModal(line) {
  const item = findItem(line.ref);
  if (!item) return '';
  const deal = line.deal;
  const groupPrice = (Number(deal.dealPriceCents) || 0) / 100;
  return `
    <div class="mp-modal-overlay" onclick="mpCancelDraft()">
      <div class="mp-modal" onclick="event.stopPropagation()" role="dialog" aria-modal="true">
        <div class="mp-modal-header">
          <div>
            <div class="mp-modal-title">${htmlEscape(line.name)} <span class="mp-deal-badge">Aktion</span></div>
            <div class="mp-modal-subtitle">
              ${htmlEscape(deal.dealName || '')} · Aktionspreis ${fmtEUR(groupPrice)}
              ${dietTagBadges(item.dietaryTags)}
            </div>
            ${item.description ? `<div class="mp-modal-desc">${htmlEscape(item.description)}</div>` : ''}
          </div>
          <button type="button" class="mp-modal-close" onclick="mpCancelDraft()" aria-label="Schließen">✕</button>
        </div>
        <div class="mp-modal-body">${renderModalBodyInner(line, item)}</div>
        ${renderModalFooterInner(line, item)}
      </div>
    </div>`;
}

/** Öffnet das gewohnte Anpassen-Modal im DEAL-Kontext (aus deals-guest.js).
 *  dealCtx = { dealDetail, groupId, dealName, lineUid }. */
export function mpOpenDealDraft(itemId, dealCtx) {
  const item = findItem(itemId);
  if (!item || !dealCtx || !dealCtx.dealDetail) { showToast('Gericht nicht gefunden.', 'error'); return; }
  const meta = item.meta || {};
  const group = (dealCtx.dealDetail.groups || []).find(g => g.id === dealCtx.groupId) || null;
  const rules = (group && group.rules) || {};
  const draft = {
    lineId: '__draft__', ref: item.id, name: item.name, category: item.category, qty: 1,
    unitPrice: Number(item.price) || 0,
    bun: null, patty: null, dressing: null, milk: null,
    removedIngredients: [], extras: [], sauces: [], side: null, sideSauces: [],
    dealDrink: null,
    deal: {
      dealId: dealCtx.dealDetail.id, dealName: dealCtx.dealName || dealCtx.dealDetail.name,
      groupId: dealCtx.groupId, lineUid: dealCtx.lineUid,
      dealPriceCents: group ? Number(group.deal_price_cents) : 0,
      rules, dealAddons: dealCtx.dealDetail.addons || [], dealDetail: dealCtx.dealDetail
    }
  };
  // Default-Brötchen aus der Whitelist (Burger) — im Deal inklusive (extra 0).
  if (meta.isBurger && meta.bunGroup) {
    let buns = optionsByGroup(meta.bunGroup);
    const wl = rules.bun_whitelist;
    if (Array.isArray(wl) && wl.length) {
      const norm = s => String(s || '').toLowerCase().trim();
      const f = buns.filter(b => wl.map(norm).some(w => norm(b.name).includes(w) || w.includes(norm(b.name))));
      if (f.length) buns = f;
    }
    const def = buns.find(b => b.meta && b.meta.isDefault) || buns[0];
    if (def) draft.bun = { id: def.id, name: def.name, extra: 0 };
  }
  // Standard-Dressing vorbelegen (Bowl/Salat).
  if (meta.requiresDressing && meta.dressingGroup) {
    const dr = optionsByGroup(meta.dressingGroup);
    const defD = dr.find(d => d.meta && d.meta.isDefault) || dr[0];
    if (defD) draft.dressing = { id: defD.id, name: defD.name };
  }
  recalcLine(draft);
  _state.draftLine = draft;
  softRefresh();
}

/** Beilage im Deal → schreibt den NORMALEN Key line.side {id,name,price}; die
 *  Beilagen-Stufe (priceCents) ist der im Deal-Preis eingerechnete Aufpreis. */
export function mpSetDealSide(itemId, priceCents, addonId) {
  const line = _state.draftLine; if (!line || !line.deal) return;
  if (itemId) {
    const it = findItem(itemId);
    // S244 Cut B — `addonId` mitschreiben: der Deal-Beilagenpreis kommt aus der
    // Add-on-STUFE (deal_addons.price_cents), nicht aus menu_items. Der
    // Tisch-Pfad rechnet serverseitig nach und braucht dafür die Stufe.
    line.side = { id: itemId, name: (it && it.name) || 'Beilage',
                  price: (Number(priceCents) || 0) / 100, addonId: addonId || null };
  } else {
    line.side = null;
    line.sideSauces = [];   // „Ohne"/Wechsel → Beilagen-Dips zurücksetzen
  }
  recalcLine(line); softRefresh();
}

/** Beilagen-Dip im Deal → NORMALER Key line.sideSauces (max. 2, inkl. 0 €). */
export function mpToggleDealSideSauce(sauceId) {
  const line = _state.draftLine; if (!line || !line.deal || !line.side) return;
  const sel = line.sideSauces || (line.sideSauces = []);
  const idx = sel.findIndex(x => x.id === sauceId);
  if (idx >= 0) { sel.splice(idx, 1); }
  else {
    if (sel.length >= 2) { showToast('Maximal 2 Soßen zur Beilage.', 'error'); return; }
    const s = findItem(sauceId);
    sel.push({ id: sauceId, name: (s && s.name) || 'Soße', price: 0 });
  }
  recalcLine(line); softRefresh();
}
export function mpSetDealDrink(addonId, itemId, priceCents) {
  const line = _state.draftLine; if (!line || !line.deal) return;
  line.dealDrink = (addonId && itemId) ? { addonId, itemId, price: (Number(priceCents) || 0) / 100 } : null;
  recalcLine(line); softRefresh();
}

export function mpConfirmDealDraft() {
  const line = _state.draftLine;
  if (!line || !line.deal) return;
  const item = findItem(line.ref);
  const meta = (item && item.meta) || {};
  if (meta.isBurger && !line.bun) { showToast('Bitte ein Brötchen wählen.', 'error'); return; }
  if (meta.isBowl && meta.requiresPatty && !line.patty) { showToast('Bitte ein Patty/Topping wählen.', 'error'); return; }
  if (meta.isBowl && meta.requiresDressing && !line.dressing) { showToast('Bitte ein Dressing wählen.', 'error'); return; }
  const drafts = _dealDraftsFromLine(line);
  if (!drafts || !drafts.length) { showToast('Konnte nicht übernommen werden.', 'error'); return; }

  // S198 Cut b3 — Solo-Re-Edit einer persistierten Deal-Position: NICHT
  // addDealPosition (das legt eine NEUE Position an), sondern den Callback
  // (→ update_deal_position RPC über beide Zeilen) rufen. Validierungen oben
  // (Bun/Patty/Dressing) laufen identisch mit.
  if (_state.soloEdit) {
    const onConfirm = _state.soloEdit.onConfirm;
    const payload = { drafts, lineUid: line.deal.lineUid };
    closeSoloConfigurator();          // Host abbauen + soloEdit/draftLine clearen
    if (typeof onConfirm === 'function') {
      Promise.resolve(onConfirm(payload)).catch(err => console.error('[soloDealConfirm]', err));
    }
    return;
  }

  const menu = getMenu();
  const bits = [item ? item.name : 'Gericht'];
  if (line.side) bits.push(line.side.name || 'Beilage');
  if (line.dealDrink) bits.push((menu.find(m => m.id === line.dealDrink.itemId) || {}).name || 'Drink');
  // S244 Cut B — Tisch-Pfad: die Zeile geht an den Server, nicht in die lokale
  // Vorschau. Preise rechnet dort `price_order_line` neu.
  if (_state.onLineConfirmed) {
    const req = buildCartRequestFromLine(line);
    _state.draftLine = null;
    softRefresh();
    Promise.resolve(_state.onLineConfirmed(req)).catch(e => console.error('[onLineConfirmed]', e));
    return;
  }
  if (typeof addDealPosition === 'function') {
    addDealPosition({
      dealId: line.deal.dealId, dealName: line.deal.dealName, lineUid: line.deal.lineUid,
      summary: bits.join(' · '), drafts, totalCents: dealDraftsTotalCents(drafts)
    });
  }
  _state.draftLine = null;
  softRefresh();
  showToast('Aktions-Menü hinzugefügt.', 'success');
}

// renderModalFooter ist pure und kommt direkt aus dem Sub-Modul.
// (Re-Imported am Modul-Anfang.)

function renderSpecialRequests() {
  return `
    <div class="mp-special">
      <label for="mp-special-input" class="mp-special-label">Sonderwünsche / Allergien</label>
      <textarea id="mp-special-input"
        class="mp-special-input"
        rows="2"
        placeholder="z.B. medium statt durch, Nuss-Allergie, ohne Eis ..."
        oninput="mpSetSpecialRequests(this.value)">${htmlEscape(_state.specialRequests)}</textarea>
    </div>
  `;
}

function renderSummary() {
  const total = subtotal();
  return `
    <div class="mp-summary">
      <div class="mp-summary-label">Vorbestellung gesamt</div>
      <div class="mp-summary-total">${fmtEUR(total)}</div>
    </div>
  `;
}
// -----------------------------------------------------------------------------
// Backward-Compat: alte preorder-Daten (v1: [{id,name,price,qty}])
// werden im Admin-Cockpit weiterhin lesbar bleiben. Hier nur Hilfsfunktion.
// -----------------------------------------------------------------------------
export function formatOrderForAdmin(preorder) {
  if (!preorder) return '';
  if (Array.isArray(preorder)) {
    // alte Struktur
    return preorder.map(p => `${p.qty}× ${p.name} (${fmtEUR(p.price)})`).join(', ');
  }
  if (preorder.version === 2 && Array.isArray(preorder.items)) {
    const parts = preorder.items.map(it => {
      let s = `${it.qty}× ${it.name}`;
      if (it.bun) s += ` · Brötchen: ${it.bun.name}`;
      if (it.patty) s += ` · Patty: ${it.patty.name}`;
      if (it.dressing) s += ` · Dressing: ${it.dressing.name}`;
      if (it.removedIngredients && it.removedIngredients.length) {
        const visible = it.removedIngredients.filter(x => x !== '__patty__');
        if (visible.length) s += ` · OHNE: ${visible.join(', ')}`;
        if (it.removedIngredients.includes('__patty__') && !it.patty) s += ` · OHNE Patty`;
      }
      if (it.extras && it.extras.length) s += ` · Extras: ${it.extras.map(e => e.name).join(', ')}`;
      // S229 T1 — sides[] (Mehrfach) tolerant lesen, Legacy-side (Objekt) als Fallback;
      // S229 T2 — Operator-Text ohne Beilagen-Einzelpreis (Positionspreis unten reicht).
      const sidesArr = Array.isArray(it.sides) ? it.sides : (it.side ? [it.side] : []);
      if (sidesArr.length) {
        s += ` · MIT Beilage: ${sidesArr.map(x => x.name).join(', ')}`;
        if (it.sideSauces && it.sideSauces.length) {
          s += ` mit Soße(n): ${it.sideSauces.map(e => e.name).join(', ')}`;
        }
      }
      if (it.sauces && it.sauces.length) s += ` · Soßen: ${it.sauces.map(e => e.name).join(', ')}`;
      s += ` — ${fmtEUR(it.lineTotal)}`;
      return s;
    });
    if (preorder.specialRequests) parts.push(`Sonderwunsch: ${preorder.specialRequests}`);
    return parts.join('\n');
  }
  return JSON.stringify(preorder);
}

// -----------------------------------------------------------------------------
// Backward-Compat: window-Exposes für inline-onclick-Handler
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    initMenuPicker,
    getOrder,
    resetMenuPicker,
    validateOrder,
    formatOrderForAdmin,
    addDealPosition,        // S169 P3 — von deals-guest.js aufgerufen
    removeDealPreview,
    getDealOrderItems,      // S169 P4 — Deal-Komponenten für die p_items
    // S197 Cut b2 — Solo-Konfigurator (Re-Edit aus Tisch-Warenkorb)
    openSoloConfigurator,
    closeSoloConfigurator,
    // S198 Cut b3 — Solo-DEAL-Konfigurator (Deal-Position bearbeiten)
    openDealSoloConfigurator,

    mpOpenDealDraft,        // S169 P3-Rework — Deal-Dish → gewohntes Anpassen-Modal
    mpConfirmDealDraft, mpSetDealSide, mpSetDealDrink, mpToggleDealSideSauce,
    mpSetDietFilter,
    mpToggleCategory,
    mpAddItem,
    mpEditLine,
    mpConfirmDraft,
    mpCancelDraft,
    mpRemoveLine,
    mpChangeQty,
    mpSelectBun,
    mpSelectPatty,
    mpSelectDressing,
    mpSelectMilk,
    mpStrikeIngredient,
    mpTogglePatty,
    mpOpenPattySwap,
    mpClosePattySwap,
    mpSwapPatty,
    mpToggleExtra,
    mpToggleSauce,
    mpToggleSide,
    mpToggleSideSauce,
    mpSetSpecialRequests,
    mpSetLineNote
  });
}

// -----------------------------------------------------------------------------
// Test-Hook-Exports (additiv, zur Laufzeit No-Op) — Pattern aus datev-export.js,
// day-close.js, orders.js, settings.js, takeaway-form.js. Memory-Lesson
// `feedback_gg_hard_way`: sauberer Test-Vertrag statt Reflection auf private
// Symbols. Wird nur von Vitest importiert.
// -----------------------------------------------------------------------------

/** Setzt _state-Felder gezielt für Tests (lines, dietFilter, specialRequests).
 *  Nicht-übergebene Felder bleiben unverändert. */
function _setStateForTests(patch) {
  if (!patch || typeof patch !== 'object') return;
  if (Array.isArray(patch.lines)) _state.lines = patch.lines;
  if (typeof patch.dietFilter === 'string') _state.dietFilter = patch.dietFilter;
  if (typeof patch.specialRequests === 'string') _state.specialRequests = patch.specialRequests;
  if (patch.draftLine !== undefined) _state.draftLine = patch.draftLine;
  // S197-Hotfix2 — Operator-Kontext testbar machen (rootId/soloEdit).
  if (typeof patch.rootId === 'string') _state.rootId = patch.rootId;
  if (patch.soloEdit !== undefined) _state.soloEdit = patch.soloEdit;
}

/** Setzt _state komplett zurück auf Defaults (wie nach initMenuPicker). */
function _resetStateForTests() {
  _state.lines = [];
  _state.draftLine = null;
  _state.specialRequests = '';
  _state.dietFilter = 'all';
  _state.openCategories = new Set();   // S169 — Rubriken-Übersicht (alle zu)
  _state.dealPreviews = [];
  _state.rootId = 'menu-picker-root';  // S197-Hotfix2 — Operator-Kontext zurücksetzen
  _state.soloEdit = null;
}

export {
  recalcLine as _recalcLine,
  subtotal as _subtotal,
  itemsByCategory as _itemsByCategory,
  itemsBySubcategory as _itemsBySubcategory,
  optionsByGroup as _optionsByGroup,
  addonsByGroup as _addonsByGroup,
  findItem as _findItem,
  dietTagBadges as _dietTagBadges,
  renderBrowseCard as _renderBrowseCard,
  renderModalBodyInner as _renderModalBodyInner,   // S197-Hotfix2 — softRefresh-Body-Ersatz
  _dealLineFromSelection,                          // S198 Cut b3 — Test-Hook (selection → Draft-Line)
  _setStateForTests,
  _resetStateForTests
};
